import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Input, Row, Table } from "reactstrap";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import DeleteModal from "components/Common/DeleteModal";
import Paginations from "common/Paginations";
import Spinners from "components/Common/Spinner";
import UserActiveInactive from "components/Common/UserActiveInactive";
import UserService from "Api/Services/UserService";

const ACCOUNT_VIEWS = {
    doctor: {
        key: "doctor",
        title: "Doctors",
        description: "Individual provider accounts.",
        addLabel: "Add Doctor",
        addPath: "/user-add?type=doctor",
    },
    "shared-login": {
        key: "shared-login",
        title: "Shared Logins",
        description: "Office logins that can switch between assigned doctors.",
        addLabel: "Add Shared Login",
        addPath: "/user-add?type=shared-login",
    },
};

const UserList = () => {
    document.title = "Users | Infuse One";

    const params = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialStatus = params.inactive ? "0" : "";
    const initialAccountType = searchParams.get("view") === "shared-login" ? "shared-login" : "doctor";

    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [users, setUsers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [deleteModal, setDeleteModal] = useState(false);
    const [totalPage, setTotalPage] = useState();
    const [deleteuserId, setDeleteUserId] = useState();
    const [userToToggle, setUserToToggle] = useState(null);
    const [userActiveInactiveModal, setUserActiveInactiveModal] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [globalFilter, setGlobalFilter] = useState("");
    const [status, setStatus] = useState(initialStatus);
    const [accountType, setAccountType] = useState(initialAccountType);

    const activeView = ACCOUNT_VIEWS[accountType] || ACCOUNT_VIEWS.doctor;
    document.title = `${activeView.title} | Infuse One`;

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            setCurrentPage(1);
            loadUserList(1, status, globalFilter, accountType);
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [status, globalFilter, accountType]);

    const loadUserList = async (page, selectedStatus, search, selectedAccountType) => {
        if (isLoading) return;

        setLoading(true);
        setError("");

        try {
            const response = await UserService.GetUserList(page, {
                status: selectedStatus || "",
                search: search || "",
                account_type: selectedAccountType || "doctor",
            });
            const data = await response.json();

            if (data.status) {
                setUsers(data.data);
                setCurrentPage(data.meta.current_page);
                setTotalPage(data.meta.last_page);
            } else {
                setError(data.message);
                setUsers([]);
            }
        } catch (loadError) {
            setError("Something went wrong");
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = page => {
        setCurrentPage(page);
        loadUserList(page, status, globalFilter, accountType);
    };

    const handleDelete = async userId => {
        setDeleteUserId(userId);
        setDeleteModal(true);
    };

    const handleDeleteUser = async () => {
        setLoading(true);
        try {
            const response = await UserService.DeleteUser(deleteuserId);

            if (response.status) {
                setSuccess(response.message);
                setUsers(users.filter(user => user.id !== deleteuserId));
                loadUserList(1, status, globalFilter, accountType);
                setDeleteModal(false);
            } else {
                setError(response.message);
            }
        } catch (deleteError) {
            setError("Something went wrong while deleting the user");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async userId => {
        navigate(`/user-edit/${userId}`);
    };

    const handleAssigneDrugs = async userId => {
        navigate(`/assigne-drug/${userId}`);
    };

    const handleUserActiveInactive = user => {
        setUserToToggle(user);
        setUserActiveInactiveModal(true);
    };

    const handleActiveInactiveUser = async () => {
        if (!userToToggle) return;
        setLoading(true);
        try {
            const response = await UserService.ChangeUserStatus(userToToggle.id);
            if (response.status) {
                setUsers(users.map(user =>
                    user.id === userToToggle.id ? { ...user, is_active: !userToToggle.is_active } : user
                ));
                setSuccess(response.message);
                setUserActiveInactiveModal(false);
                loadUserList(currentPage, status, globalFilter, accountType);
            } else {
                setError(response.message);
            }
        } catch (toggleError) {
            setError("Something went wrong while updating the user status.");
        } finally {
            setLoading(false);
            setUserActiveInactiveModal(false);
            setUserToToggle(null);
        }
    };

    const handleAccountTypeChange = nextAccountType => {
        setAccountType(nextAccountType);
        setSearchParams(nextAccountType === "shared-login" ? { view: "shared-login" } : { view: "doctor" });
    };

    return (
        <React.Fragment>
            <DeleteModal
                show={deleteModal}
                onDeleteClick={handleDeleteUser}
                onCloseClick={() => setDeleteModal(false)}
            />
            <UserActiveInactive
                show={userActiveInactiveModal}
                onChangeClick={() => handleActiveInactiveUser()}
                onCloseClick={() => setUserActiveInactiveModal(false)}
                isActive={userToToggle ? userToToggle?.is_active : false}
                modelType="user"
            />

            <div className="page-content">
                <Container fluid>
                    <div className="container-fluid">
                        <div
                            className="page-title-box"
                            style={{ padding: "0 0 0.5rem", marginBottom: "0.5rem" }}
                        >
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                <div className="d-flex flex-wrap align-items-center gap-3">
                                    <h2 className="page-title mb-0">{activeView.title}</h2>
                                    <div className="d-flex flex-wrap gap-2">
                                        {Object.values(ACCOUNT_VIEWS).map(view => (
                                            <button
                                                key={view.key}
                                                type="button"
                                                className={`btn btn-sm ${accountType === view.key ? "btn-secondary" : "btn-light"}`}
                                                onClick={() => handleAccountTypeChange(view.key)}
                                            >
                                                {view.title}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="d-flex gap-2">
                                    <Link to={activeView.addPath} className="btn btn-secondary btn-rounded">
                                        <i className="mdi mdi-plus"></i>{activeView.addLabel}
                                    </Link>
                                </div>
                            </div>
                            <div className="text-body-secondary fs-6 mt-1">
                                {activeView.description}
                            </div>
                        </div>
                        <Row>
                            <Col lg={12}>
                                <Card className={isLoading ? "commonboxform pb-5" : "commonboxform"}>
                                    <CardBody style={{ padding: "1.5rem 1.75rem 1.75rem" }}>
                                        {error ? (
                                            <div className="alert alert-danger mb-4">{error}</div>
                                        ) : null}

                                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4 pb-3 border-bottom">
                                            <div className="d-flex align-items-center gap-3 flex-nowrap w-100" style={{ maxWidth: "760px" }}>
                                                <div
                                                    className="position-relative"
                                                    style={{ flex: "1 1 auto", minWidth: "0" }}
                                                >
                                                    <Input
                                                        type="text"
                                                        name="search"
                                                        className="form-control search-box px-3"
                                                        placeholder={`Search ${activeView.title.toLowerCase()}...`}
                                                        value={globalFilter}
                                                        onChange={event => setGlobalFilter(event.target.value)}
                                                    />
                                                </div>
                                                <div style={{ width: "180px", flex: "0 0 180px" }}>
                                                    <label className="visually-hidden" htmlFor="userStatusFilter">Status</label>
                                                    <select
                                                        id="userStatusFilter"
                                                        className="form-select"
                                                        name="status"
                                                        value={status}
                                                        onChange={event => setStatus(event.target.value)}
                                                    >
                                                        <option value="">All statuses</option>
                                                        <option value="1">Active</option>
                                                        <option value="0">Inactive</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="text-muted small">
                                                {users.length} result{users.length === 1 ? "" : "s"} on this page
                                            </div>
                                        </div>

                                        <div className="table-responsive">
                                            <Table className="table align-middle mb-0">
                                                <thead>
                                                    <tr className="text-center">
                                                        <th className="text-start">User</th>
                                                        <th>Practice</th>
                                                        <th>{accountType === "shared-login" ? "Doctors" : "Shared Access"}</th>
                                                        <th>Status</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="relativecls">
                                                    {isLoading ? <Spinners setLoading={setLoading} /> : (
                                                        <>
                                                            {users.map(user => (
                                                                <tr key={user.id} className="text-center">
                                                                    <td className="text-start py-2">
                                                                        <div className="fw-semibold">{user.name}</div>
                                                                        <div className="text-muted small">
                                                                            {user.username ? `@${user.username}` : "No username"}
                                                                            {user.email ? ` • ${user.email}` : ""}
                                                                        </div>
                                                                    </td>
                                                                    <td className="py-2">
                                                                        {accountType === "shared-login"
                                                                            ? (user.doctor_group?.name || user.doctor_group_name || user.practicename || "-")
                                                                            : (user.practicename || "-")}
                                                                    </td>
                                                                    <td className="py-2">
                                                                        {accountType === "shared-login"
                                                                            ? (
                                                                                user.managed_doctors && user.managed_doctors.length > 0
                                                                                    ? user.managed_doctors.map(doctor => doctor.name).join(", ")
                                                                                    : "-"
                                                                            )
                                                                            : (
                                                                                user.shared_logins && user.shared_logins.length > 0
                                                                                    ? user.shared_logins.map(login => login.name).join(", ")
                                                                                    : user.doctor_group?.name || user.doctor_group_name || "-"
                                                                            )}
                                                                    </td>
                                                                    <td className="py-2">
                                                                        <a className="px-3 text-center" onClick={() => handleUserActiveInactive(user)}>
                                                                            {user.is_active && user.is_active == 1 ? (
                                                                                <span className="bg-success badge bg-secondary">Active</span>
                                                                            ) : (
                                                                                <span className="bg-danger badge bg-secondary">Inactive</span>
                                                                            )}
                                                                        </a>
                                                                    </td>
                                                                    <td className="py-2">
                                                                        <div className="d-flex gap-3 justify-content-center align-items-center">
                                                                            <a className="text-success" onClick={() => handleUpdate(user.id)}>
                                                                                <i className="mdi mdi-pencil font-size-18" title="Edit"></i>
                                                                            </a>
                                                                            {!user.is_group_login ? (
                                                                                <a className="text-info" onClick={() => handleAssigneDrugs(user.id)}>
                                                                                    <i className="bx bxs-capsule font-size-18" title="Assign Drug"></i>
                                                                                </a>
                                                                            ) : null}
                                                                            <a className="text-danger" onClick={() => handleDelete(user.id)}>
                                                                                <i className="mdi mdi-delete font-size-18" title="Delete"></i>
                                                                            </a>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </>
                                                    )}
                                                </tbody>
                                            </Table>
                                            <h5 className="text-center mt-4">
                                                {users.length === 0 && !isLoading ? "Data Not Found!" : ""}
                                            </h5>
                                            {users.length !== 0 && !isLoading ? (
                                                <Paginations
                                                    currentPage={currentPage}
                                                    totalPages={totalPage}
                                                    onPageChange={handlePageChange}
                                                />
                                            ) : ""}
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </div>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default UserList;
